import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../services/supabase';

/**
 * The one-time "We've updated our Creator Terms" screen (O4), built dark.
 *
 * Nothing shows until staff set platform_settings.creator_terms_version; the
 * database decides everything through creator_terms_state():
 *  - needs_acceptance: some page this creator owns hasn't accepted the current
 *    version. Never true for staff, whether on view-as or their own login.
 *  - can_defer: before creator_terms_effective_at the screen offers "Not now",
 *    which hides it until the next day. From the effective date on it keeps
 *    showing until they agree. It never blocks the dashboard: the Terms say
 *    continued use counts as acceptance.
 *
 * accept_creator_terms() records the acceptance for every page the creator
 * owns. It takes no ids from here and refuses a view-as session.
 */

/** The public Terms page (frontend repo, src/pages/TermsOfServicePage.tsx). */
export const CREATOR_TERMS_URL = 'https://shoutout.us/terms-of-service';

/** "The dashboard screen, short version" from the Terms doc (14 Oct: no forfeiting, no time limits). */
export const CREATOR_TERMS_SUMMARY = [
  'Your page shows sponsor ads. You earn a share when visitors click them, and you can turn them off in your dashboard.',
  'Never click your own ads, or ask anyone to click them.',
  'Your fans may get sponsored emails from us, labeled as ads. You earn per click, and you can turn them off.',
  'Ad revenue pays out once your page gets 30 visits in 30 days, so share your link wherever your fans are. Until then, it’s held for you.',
  'Orders and tips need only payout setup. Haven’t finished it? Your earnings are held until you do.',
  'Sponsorship campaigns come with their own requirements, set out in each offer.',
  'Large balances may be paid in installments.',
];

export type CreatorTermsState =
  | { on: false }
  | {
      on: true;
      version: string;
      effective_on: string | null;
      can_defer: boolean;
      needs_acceptance: boolean;
      staff: boolean;
    };

/** null when the state couldn't be read: show nothing rather than guess. */
export async function getCreatorTermsState(): Promise<CreatorTermsState | null> {
  const { data, error } = await supabase.rpc('creator_terms_state');
  if (error || !data || typeof data !== 'object') return null;
  return data as CreatorTermsState;
}

export async function acceptCreatorTerms(
  version: string,
  method: 'screen' | 'signup'
): Promise<CreatorTermsState | null> {
  const { data, error } = await supabase.rpc('accept_creator_terms', {
    p_version: version,
    p_method: method,
    p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
  });
  if (error) throw error;
  return (data as CreatorTermsState) ?? null;
}

export type SignupTermsResult = { version: string; pages: number; recorded: number };

/**
 * The Terms version a signup agrees to right now (13 Oct 2026): the database
 * decides (creator_terms_version when set, else the published page's key).
 * /start reads it when its last step opens, to pin a later retry to it.
 * null when it couldn't be read.
 */
export async function getCreatorTermsSignupVersion(): Promise<string | null> {
  const { data, error } = await supabase.rpc('creator_terms_signup_version');
  return !error && typeof data === 'string' && data ? data : null;
}

/**
 * /start's "I agree to the ShoutOut Creator Terms" box, ticked and finished.
 * Records every page the creator owns; takes no ids from here. With
 * `version` (a retry), the database refuses it unless it's still the current
 * one. Throws unless every owned page is on record.
 */
export async function acceptCreatorTermsAtSignup(version: string | null): Promise<SignupTermsResult> {
  const { data, error } = await supabase.rpc('accept_creator_terms_at_signup', {
    p_version: version,
    p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
  });
  if (error) throw error;
  const r = data as SignupTermsResult | null;
  if (!r || !r.pages || r.recorded < r.pages) throw new Error('The Terms acceptance was not recorded');
  return r;
}

/** Postgres "insufficient privilege": staff accounts, which never accept. */
export function isTermsRefusal(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === '42501';
}

/** "Not the current Terms version": the Terms changed since they agreed. */
export function isStaleTermsVersion(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === '22023';
}

/** How long a signup acceptance /start couldn't save keeps being retried. */
export const SIGNUP_PENDING_MS = 7 * 24 * 60 * 60 * 1000;

function signupPendingKey(userId: string): string {
  return `so_terms_signup_pending:${userId}`;
}

/**
 * /start couldn't record the ticked box: remember the version it agreed to,
 * and the workspace tries again (below).
 */
export function markSignupTermsPending(userId: string, version: string, now = Date.now()): void {
  try {
    localStorage.setItem(signupPendingKey(userId), JSON.stringify({ at: now, version }));
  } catch {
    /* private mode: nothing to retry from */
  }
}

/**
 * Retry a signup acceptance /start couldn't save, once the workspace opens,
 * for the version the creator agreed to. The database refuses it once the
 * Terms have changed (the O4 screen asks about the new ones instead). Clears
 * on success, a refusal, a changed version, or after a week. Never throws.
 */
export async function retryPendingSignupTerms(userId: string, now = Date.now()): Promise<void> {
  let raw: string | null;
  try {
    raw = localStorage.getItem(signupPendingKey(userId));
  } catch {
    return;
  }
  if (!raw) return;
  const clear = () => {
    try {
      localStorage.removeItem(signupPendingKey(userId));
    } catch {
      /* nothing to clear */
    }
  };
  let pending: { at?: unknown; version?: unknown } | null = null;
  try {
    pending = JSON.parse(raw);
  } catch {
    pending = null;
  }
  const at = Number(pending?.at);
  const version = typeof pending?.version === 'string' ? pending.version : '';
  if (!version || !Number.isFinite(at) || now - at > SIGNUP_PENDING_MS) {
    clear();
    return;
  }
  try {
    await acceptCreatorTermsAtSignup(version);
    clear();
  } catch (err) {
    if (isTermsRefusal(err) || isStaleTermsVersion(err)) clear();
  }
}

/** Today in Central time, the same calendar the effective date uses. */
function centralToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function snoozeKey(userKey: string, version: string): string {
  return `so_terms_not_now:${userKey}:${version}`;
}

function snoozedToday(userKey: string, version: string): boolean {
  try {
    return localStorage.getItem(snoozeKey(userKey, version)) === centralToday();
  } catch {
    return false;
  }
}

export type CreatorTermsPrompt = {
  /** Show the sheet now. */
  show: boolean;
  state: Extract<CreatorTermsState, { on: true }> | null;
  accepting: boolean;
  error: string | null;
  agree: () => Promise<void>;
  /** "Not now": only offered before the effective date; back the next day. */
  notNow: () => void;
  /** The first read has answered (or there's nothing to read), so `show` means something. */
  checked: boolean;
};

/**
 * Whether the updated-Terms sheet is up, for other one-time sheets in the
 * shell (the creator directory notice) that must wait their turn. The Terms
 * sheet reports here; anything else reads it.
 */
export const TERMS_SHEET_EVENT = 'creator-terms-sheet';
let termsSheet = { checked: false, open: false };

export function reportTermsSheet(next: { checked: boolean; open: boolean }): void {
  if (next.checked === termsSheet.checked && next.open === termsSheet.open) return;
  termsSheet = next;
  window.dispatchEvent(new CustomEvent(TERMS_SHEET_EVENT));
}

export function useTermsSheetState(): { checked: boolean; open: boolean } {
  const [value, setValue] = useState(termsSheet);
  useEffect(() => {
    const on = () => setValue(termsSheet);
    window.addEventListener(TERMS_SHEET_EVENT, on);
    on();
    return () => window.removeEventListener(TERMS_SHEET_EVENT, on);
  }, []);
  return value;
}

/**
 * @param userKey whose "Not now" this is (the signed-in user's id).
 * @param blocked true while staff are viewing: never show, never accept.
 */
export function useCreatorTermsPrompt(userKey: string | null | undefined, blocked: boolean): CreatorTermsPrompt {
  const [state, setState] = useState<Extract<CreatorTermsState, { on: true }> | null>(null);
  const [snoozed, setSnoozed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // An open workspace re-asks when the creator comes back to it and every 15
  // minutes, so a "Not now" ends when the Central day changes, the effective
  // date takes over, and a newly enabled version shows without a reload.
  const [tick, setTick] = useState(0);
  const [checked, setChecked] = useState(false);
  // Each read and each acceptance takes a number; a read only lands if
  // nothing newer has started since, so an older re-check can never reopen
  // the sheet after "Agree and continue" saved.
  const seqRef = useRef(0);

  useEffect(() => {
    const bump = () => {
      if (document.visibilityState === 'visible') setTick((t) => t + 1);
    };
    const timer = window.setInterval(() => setTick((t) => t + 1), 15 * 60 * 1000);
    document.addEventListener('visibilitychange', bump);
    window.addEventListener('focus', bump);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', bump);
      window.removeEventListener('focus', bump);
    };
  }, []);

  useEffect(() => {
    if (!userKey || blocked) {
      setState(null);
      // Not checked until a real (unblocked) read answers: while the staff
      // lookup is pending this is blocked, and the directory notice must not
      // open ahead of Terms a creator still has to see.
      setChecked(false);
      return;
    }
    let cancelled = false;
    const run = ++seqRef.current;
    void (async () => {
      // A signup acceptance /start couldn't save goes first, so the state
      // read below already counts it.
      await retryPendingSignupTerms(userKey);
      const next = await getCreatorTermsState();
      if (!cancelled) setChecked(true);
      // A failed re-check keeps the last answer: never pull the sheet out
      // from under someone reading it.
      if (cancelled || next === null || run !== seqRef.current) return;
      const on = next.on ? next : null;
      setState(on);
      setSnoozed(!!on && snoozedToday(userKey, on.version));
    })();
    return () => {
      cancelled = true;
    };
  }, [userKey, blocked, tick]);

  const agree = useCallback(async () => {
    if (!state || blocked) return;
    setAccepting(true);
    setError(null);
    seqRef.current++;
    try {
      const next = await acceptCreatorTerms(state.version, 'screen');
      seqRef.current++;
      setState(next && next.on ? next : null);
    } catch {
      setError('We couldn’t save that. Check your connection and try again.');
    } finally {
      setAccepting(false);
    }
  }, [state, blocked]);

  const notNow = useCallback(() => {
    if (!state || !state.can_defer || !userKey) return;
    try {
      localStorage.setItem(snoozeKey(userKey, state.version), centralToday());
    } catch {
      /* private mode: it just shows again next time */
    }
    setSnoozed(true);
  }, [state, userKey]);

  const show =
    !blocked &&
    !!state &&
    state.needs_acceptance &&
    !state.staff &&
    // From the effective date on, "Not now" no longer hides it.
    (!state.can_defer || !snoozed);

  return { show, state, accepting, error, agree, notNow, checked };
}
